import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { setupMfa, activateMfa, disableMfa, changePassword, getMe, setToken, getMyEmails, addMyEmail, updateMyEmail, deleteMyEmail } from '../api/client'
import { patchMe } from '../api/auth'
import type { UserEmailOut } from '../api/users'
import { useStore } from '../store'
import i18n from '../i18n'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
      {children}
    </div>
  )
}

function SuccessBanner({ msg, onDismiss }: { msg: string; onDismiss: () => void }) {
  return (
    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 text-green-700 dark:text-green-300 rounded-lg px-4 py-2 text-sm flex justify-between items-center">
      {msg}
      <button onClick={onDismiss} className="ml-4 text-green-400 hover:text-green-600">✕</button>
    </div>
  )
}

// ── MFA setup flow ──────────────────────────────────────────────
function MfaSetup({ onActivated }: { onActivated: () => void }) {
  const { t } = useTranslation()
  const [step, setStep] = useState<'idle' | 'scan' | 'confirm'>('idle')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const startSetup = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await setupMfa()
      setQrCode(data.qr_code)
      setSecret(data.secret)
      setStep('scan')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('account_settings.mfa_setup.error_load_qr'))
    } finally {
      setLoading(false)
    }
  }

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const token = await activateMfa(secret, code)
      if (token) setToken(token)
      onActivated()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('account_settings.mfa_setup.error_activate'))
    } finally {
      setLoading(false)
    }
  }

  if (step === 'idle') {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('account_settings.mfa_setup.description')}
        </p>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          onClick={startSetup}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          {loading ? t('common.loading') : t('account_settings.mfa_setup.enable_button')}
        </button>
      </div>
    )
  }

  if (step === 'scan') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t('account_settings.mfa_setup.scan_instruction')}
        </p>
        <img
          src={`data:image/png;base64,${qrCode}`}
          alt={t('account_settings.mfa_setup.qr_alt')}
          className="w-48 h-48 border border-gray-200 dark:border-gray-700 rounded-lg"
        />
        <details className="text-xs text-gray-400">
          <summary className="cursor-pointer hover:text-gray-600">{t('account_settings.mfa_setup.manual_entry_toggle')}</summary>
          <code className="block mt-2 bg-gray-100 dark:bg-gray-800 rounded px-2 py-1 font-mono break-all select-all">{secret}</code>
        </details>
        <button
          onClick={() => { setStep('confirm'); setError(null) }}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          {t('common.next')}
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={confirm} className="space-y-4">
      <p className="text-sm text-gray-600">
        {t('account_settings.mfa_setup.confirm_instruction')}
      </p>
      <input
        type="text"
        inputMode="numeric"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        placeholder="000000"
        autoFocus
        className="w-36 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          {loading ? t('common.busy') : t('common.confirm')}
        </button>
        <button
          type="button"
          onClick={() => { setStep('scan'); setError(null) }}
          className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-4 py-2"
        >
          {t('common.back')}
        </button>
      </div>
    </form>
  )
}

// ── MFA disable ──────────────────────────────────────────────────
function MfaDisable({ onDisabled }: { onDisabled: () => void }) {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await disableMfa(password)
      onDisabled()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('account_settings.mfa_disable.error_disable'))
    } finally {
      setLoading(false)
    }
  }

  if (!confirming) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-green-600 text-sm font-medium">{t('account_settings.mfa_disable.status_enabled')}</span>
        </div>
        <button
          onClick={() => setConfirming(true)}
          className="text-sm text-red-500 hover:text-red-700 underline"
        >
          {t('account_settings.mfa_disable.disable_button')}
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleDisable} className="space-y-3">
      <p className="text-sm text-gray-600">{t('account_settings.mfa_disable.password_instruction')}</p>
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t('account_settings.mfa_disable.password_placeholder')}
        autoFocus
        className="w-full max-w-xs border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 dark:bg-gray-800 dark:text-gray-100"
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading || !password}
          className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          {loading ? t('common.busy') : t('account_settings.mfa_disable.disable_button')}
        </button>
        <button
          type="button"
          onClick={() => { setConfirming(false); setError(null) }}
          className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-4 py-2"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  )
}

// ── Change password ──────────────────────────────────────────────
function ChangePassword() {
  const { t } = useTranslation()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (next.length < 8) { setError(t('account_settings.change_password.error_min_length')); return }
    setLoading(true)
    setError(null)
    try {
      await changePassword(current, next)
      setSuccess(true)
      setCurrent('')
      setNext('')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('account_settings.change_password.error_change'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {success && <SuccessBanner msg={t('account_settings.change_password.success')} onDismiss={() => setSuccess(false)} />}
      <div className="grid gap-3 max-w-xs">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('account_settings.change_password.label_current')}</label>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('account_settings.change_password.label_new')}</label>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            minLength={8}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={loading || !current || !next}
        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
      >
        {loading ? t('common.busy') : t('account_settings.change_password.submit_button')}
      </button>
    </form>
  )
}

// ── E-mailadressen beheren ────────────────────────────────────────
function EmailAddressManager() {
  const { t } = useTranslation()
  const [emails, setEmails] = useState<UserEmailOut[]>([])
  const [loading, setLoading] = useState(true)
  const [newEmail, setNewEmail] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const load = async () => {
    try {
      setEmails(await getMyEmails())
    } catch { /* negeer */ }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newEmail.trim()) return
    setAdding(true)
    setError(null)
    try {
      await addMyEmail({ email: newEmail.trim(), label: newLabel.trim() || null })
      setNewEmail('')
      setNewLabel('')
      setSuccess(t('account_settings.email_manager.success_added'))
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account_settings.email_manager.error_add'))
    } finally {
      setAdding(false)
    }
  }

  const handleSetPrimary = async (id: number) => {
    setError(null)
    try {
      await updateMyEmail(id, { is_primary: true })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account_settings.email_manager.error_update'))
    }
  }

  const handleDelete = async (id: number) => {
    setError(null)
    try {
      await deleteMyEmail(id)
      setSuccess(t('account_settings.email_manager.success_deleted'))
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('account_settings.email_manager.error_delete'))
    }
  }

  if (loading) return <p className="text-sm text-gray-400">{t('common.loading')}</p>

  return (
    <div className="space-y-4">
      {success && <SuccessBanner msg={success} onDismiss={() => setSuccess(null)} />}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {emails.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">{t('account_settings.email_manager.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {emails.map(e => (
            <li key={e.id} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{e.email}</span>
                {e.label && <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">({e.label})</span>}
                {e.is_primary && e.status === 'approved' && (
                  <span className="ml-2 text-xs font-medium text-indigo-600 dark:text-indigo-400">{t('common.primary')}</span>
                )}
                {e.status === 'pending_user' && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full">
                    ✉ {t('account_settings.email_manager.status_pending_user')}
                  </span>
                )}
                {e.status === 'pending_admin' && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded-full">
                    ⏳ {t('account_settings.email_manager.status_pending_admin')}
                  </span>
                )}
                {e.status === 'rejected' && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded-full">
                    ✕ {t('account_settings.email_manager.status_rejected')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!e.is_primary && e.status === 'approved' && (
                  <button
                    onClick={() => handleSetPrimary(e.id)}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    {t('common.make_primary')}
                  </button>
                )}
                {emails.length > 1 && (
                  <button
                    onClick={() => handleDelete(e.id)}
                    className="text-xs text-red-500 hover:text-red-700 hover:underline"
                  >
                    {t('common.delete')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleAdd} className="flex gap-2 flex-wrap">
        <input
          type="email"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          placeholder={t('account_settings.email_manager.placeholder_email')}
          required
          className="flex-1 min-w-0 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-gray-100"
        />
        <input
          type="text"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          placeholder={t('account_settings.email_manager.placeholder_label')}
          maxLength={100}
          className="w-36 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-gray-100"
        />
        <button
          type="submit"
          disabled={adding || !newEmail.trim()}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors shrink-0"
        >
          {adding ? t('account_settings.email_manager.adding') : t('common.add')}
        </button>
      </form>
      <p className="text-xs text-gray-400 dark:text-gray-500">
        {t('account_settings.email_manager.primary_hint')}
      </p>
    </div>
  )
}


// ── Language selector ────────────────────────────────────────────
function LanguageSelector() {
  const { t } = useTranslation()
  const { language, setLanguage } = useStore()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange = async (lang: string) => {
    setSaving(true)
    setError(null)
    try {
      await patchMe({ preferred_language: lang })
      setLanguage(lang)
      i18n.changeLanguage(lang)
    } catch {
      setError(t('account_settings.language_save_error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm text-gray-600 dark:text-gray-400">{t('account_settings.language_label')}</label>
      <select
        value={language}
        onChange={e => handleChange(e.target.value)}
        disabled={saving}
        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
      >
        <option value="nl">🇳🇱 Nederlands</option>
        <option value="en">🇬🇧 English</option>
      </select>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  )
}


// ── Main component ───────────────────────────────────────────────
export function AccountSettings() {
  const { t } = useTranslation()
  const { currentUser, setCurrentUser } = useStore()
  const [mfaSuccess, setMfaSuccess] = useState<string | null>(null)

  const refreshUser = async () => {
    try { setCurrentUser(await getMe()) } catch { /* ignore */ }
  }

  const handleMfaActivated = async () => {
    await refreshUser()
    setMfaSuccess(t('account_settings.mfa_activated_success'))
  }

  const handleMfaDisabled = async () => {
    await refreshUser()
    setMfaSuccess(t('account_settings.mfa_disabled_success'))
  }

  if (!currentUser) return null

  return (
    <div className="max-w-xl space-y-6">
      {mfaSuccess && <SuccessBanner msg={mfaSuccess} onDismiss={() => setMfaSuccess(null)} />}

      <Section title={t('account_settings.section_account_details')}>
        <dl className="text-sm space-y-1.5">
          <div className="flex gap-2">
            <dt className="text-gray-500 w-28 shrink-0">{t('account_settings.label_username')}</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">{currentUser.username}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-gray-500 w-28 shrink-0">{t('account_settings.label_initials')}</dt>
            <dd className="font-mono text-gray-700 dark:text-gray-300">{currentUser.initials ?? <span className="text-gray-300 dark:text-gray-600">—</span>}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-gray-500 w-28 shrink-0">{t('account_settings.label_role')}</dt>
            <dd>{currentUser.is_admin
              ? <span className="text-purple-600 font-medium">{t('account_settings.role_admin')}</span>
              : <span className="text-gray-600 dark:text-gray-400">{t('account_settings.role_user')}</span>}
            </dd>
          </div>
        </dl>
        <p className="text-xs text-gray-400 dark:text-gray-500">{t('account_settings.initials_hint')}</p>
      </Section>

      <Section title={t('account_settings.section_emails')}>
        <EmailAddressManager />
      </Section>

      <Section title={t('account_settings.section_mfa')}>
        {currentUser.mfa_enabled
          ? <MfaDisable onDisabled={handleMfaDisabled} />
          : <MfaSetup onActivated={handleMfaActivated} />
        }
      </Section>

      <Section title={t('account_settings.section_change_password')}>
        <ChangePassword />
      </Section>

      <Section title={t('account_settings.section_language')}>
        <LanguageSelector />
      </Section>
    </div>
  )
}
